<template>
    <div>
        <v-card class="stock" outlined>
            <v-card-title class="page-heading primary">
                <v-row align="center">
                    <v-col cols="12" sm="9">{{ $t('stock.heading') }}</v-col>
                    <v-col cols="12" sm="3" class="d-flex justify-end">
                        <v-btn
                            class="minw100"
                            color="white"
                            outlined
                            @click="print"
                        >
                            <v-icon left>fal fa-print</v-icon>
                            {{ $t('general.print') }}
                        </v-btn>
                    </v-col>
                </v-row>
            </v-card-title>

            <v-card-text>
                <stock-search
                    :filter.sync="filter"
                    :query-only="false"
                />

                <v-data-table
                    class="items-table operations striped"
                    :headers="headers"
                    :items="items"
                    :options.sync="pagination"
                    :footer-props="{ 'items-per-page-options': [20, 50, 100, 200] }"
                    :items-per-page.sync="pagination.itemsPerPage"
                    :server-items-length="itemsTotal"
                    :loading="loading"
                    :mobile-breakpoint="0"
                    dense
                >
                    <template v-slot:foot="{ items }">
                        <tfoot>
                            <tr>
                                <td></td>
                                <td></td>
                                <td></td>
                                <td></td>
                                <td>{{ $t('general.total').toUpperCase() }}:</td>
                                <td>{{ summary.sellingValueBeforeTax.toFormat('0.00') }}</td>
                                <td>{{ summary.sellingValueAfterTax.toFormat('0.00') }}</td>
                                <td></td>
                            </tr>
                        </tfoot>
                    </template>
                    <template v-slot:item="{ item }">
                        <tr>
                            <td>{{ item.code }}</td>
                            <td>{{ item.name }}</td>
                            <td>{{ item.getCount() }}</td>
                            <td>{{ item.getPriceBeforeTax().toFormat('0.00') }}</td>
                            <td>{{ item.getPriceAfterTax().toFormat('0.00') }}</td>
                            <td>{{ item.getSellingValueBeforeTax().toFormat('0.00') }}</td>
                            <td>{{ item.getSellingValueAfterTax().toFormat('0.00') }}</td>
                            <td>
                                <v-btn
                                    icon
                                    color="primary"
                                    @click="goToStockItem(item)"
                                >
                                    <v-icon>fal fa-list</v-icon>
                                </v-btn>
                            </td>
                        </tr>
                    </template>
                </v-data-table>
            </v-card-text>
        </v-card>
    </div>
</template>

<script>
import ApiService from '@/services/ApiService';
import StockSearch from '@/views/shared/admin/stock/StockSearch';
import Product from '@/models/Product';
import HtmlGen from '@/wrappers/HtmlGen';

export default {
    components: {
        StockSearch,
    },

    data() {
        return {
            items: [],
            itemsTotal: 0,
            loading: false,

            filter: {
                search: null,
                countFilterValue: null,
                countFilterType: null,
            },

            headers: [
                { text: this.$t('general.code'), value: 'code' },
                { text: this.$t('general.name'), value: 'name' },
                { text: this.$t('general.in_stock'), value: 'count' },
                { text: this.$t('stock.selling_price'), value: 'selling_price', sortable: false },
                { text: this.$t('stock.selling_price_after_tax'), value: 'selling_price_after_tax', sortable: false },
                { text: this.$t('stock.selling_value'), value: 'selling_value', sortable: false },
                { text: this.$t('stock.selling_value_after_tax'), value: 'selling_value_after_tax', sortable: false },
                { text: '', sortable: false, align: 'right' }
            ],

            pagination: {
                sortBy: ['name'],
                itemsPerPage: 100,
                page: 1,
                sortDesc: [true],
            },
        };
    },

    watch: {
        pagination: {
            handler() {
                this.getItems();
            },
            deep: true
        },

        filter() {
            this.getItems();
        },
    },

    computed: {
        summary() {
            return {
                sellingValueBeforeTax: this.items.reduce((acc, cur) => acc.add(cur.getSellingValueBeforeTax()), Money.parse(0)),
                sellingValueAfterTax: this.items.reduce((acc, cur) => acc.add(cur.getSellingValueAfterTax()), Money.parse(0)),
            }
        }
    },

    methods: {
        getItems() {
            const { sortBy, sortDesc, page, itemsPerPage } = this.pagination;
            const sortField = sortBy[0] ? sortBy[0] : 'date';
            const sortOrder = sortDesc[0] ? 'descending' : 'ascending';

            let url = `/admin/stock?page=${page}&itemsPerPage=${itemsPerPage}&sortField=${sortField}&sortOrder=${sortOrder}`;

            if (this.filter) {
                Object.keys(this.filter).forEach(key => {
                    if (this.filter[key] !== undefined && this.filter[key] !== null && 
                        !(key === 'countFilterType' && this.filter.countFilterType === 'all')) {
                        url += `&${key}=${this.filter[key]}`;
                    }
                })
            }
            this.loading = true;

            ApiService.get(url).then(response => {
                this.loading = false;
                this.items = response.data.data.map(p => new Product(p));
                this.itemsTotal = response.data.total;
            });
        },

        goToStockItem(product) {
            this.$router.push({ name: 'adminStockItem', params: { id: product.id } }).catch(err => {});
        },

        print() {
            const lines = [];

            lines.push(`[HR]`);
            lines.push(`[C][H22]${this.$t('stock.heading')}`);
            lines.push(`[HR]`);
            lines.push(`[L]`);
            lines.push(`[L]${this.tenant.venue.name}`);
            lines.push(`[L]${this.$t('general.tax_id_number')}: ${this.tenant.venue.tax_id_number}`);
            lines.push(`[L]${this.$t('general.date')}: ${moment().format('DD.MM.YYYY.')}`);

            lines.push(`[L]`);

            lines.push(`[HR]`);
            lines.push(
                `[L][H00]${this.$t('general.code')}` +
                `[L]${this.$t('general.inventory_item')}` +
                `[R]${this.$t('general.in_stock')}` +
                `[R]${this.$t('stock.selling_price')}` +
                `[R]${this.$t('stock.selling_price_after_tax')}` +
                `[R]${this.$t('stock.selling_value')}` +
                `[R]${this.$t('stock.selling_value_after_tax')}`
            );
            lines.push(`[HR]`);

            this.items.filter(item => item.getCount() != 0).forEach((item, index) => {
                lines.push(
                    `[L][H00]${item.code || ''}` +
                    `[L]${item.name}` +
                    `[R]${item.getCount()} ${item.getUnitOfMeasure()}` +
                    `[R]${item.getPriceBeforeTax().toFormat('0.00')}` +
                    `[R]${item.getPriceAfterTax().toFormat('0.00')}` +
                    `[R]${item.getSellingValueBeforeTax().toFormat('0.00')}` +
                    `[R]${item.getSellingValueAfterTax().toFormat('0.00')}`
                );
            })

            lines.push(`[HR]`);

            lines.push(
                `[L][H00][L][L][L]` +
                `[R]${this.$t('general.total').toUpperCase()}:` +
                `[R]${this.summary.sellingValueBeforeTax.toFormat('0.00')}` +
                `[R]${this.summary.sellingValueAfterTax.toFormat('0.00')}`
            );

            const html = lines.map(line => HtmlGen.fromTextLine(line)).join('');
            this.$printService.printCustom(null, html, null, { choosePrinter: true })
        }
    }
};
</script>

<style lang="scss" scoped>
.stock {
    .page-heading {
        padding: 12px 16px;
        color: white;
        font-size: 16px;
        word-break: break-word;
    }

    ::v-deep .items-table {
        margin-top: 28px;

        th {
            font-size: 11px;
            padding: 0 4px;
            max-width: 80px;

            span {
                white-space: break-spaces;
            }
        }

        td {
            font-size: 13px;
            padding: 0 4px;
        }

        .separator {
            border-left: 1px solid rgba(0, 0, 0, 0.5);
            padding-left: 12px;
        }

        tfoot {
            font-weight: 500;
            background: #f3f4fb;
        }
    }
}
</style>
